// src/components/reports/ReportNotices.jsx
//
// Manage the free-form notice that renders directly under the Executive Summary
// on the client report: offers, price changes, "we've changed our support
// address". Craig's own words — there is no AI here and no draft/approve step,
// because he is the author and a draft state would be a click that protects
// nobody.
//
// TWO SCOPES, AND THEY STACK. The global notice goes to every client; a company
// notice goes to one. A client with both sees the global block first and its own
// second. Neither suppresses the other — that toggle was deferred from v1, and
// the UI says so rather than leaving Craig to discover it on a rendered report.

import { useState, useEffect, useCallback, useRef } from "react";
import { getNotices, saveNotice, clearNotice } from "../../api/monthlyReportsApi";

/* Mirrors reportNoticeService. ⚠ These are the SERVER's limits restated for a
   friendlier message — the server re-checks every one of them, so a mismatch
   here is a worse error message, never a security hole. */
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/webp";

const lbl = { display: "block", fontSize: 11, fontWeight: 700, color: "var(--text2)", marginBottom: 4 };
const card = {
  background: "var(--card,#fff)", border: "1px solid var(--border)", borderRadius: 10,
  padding: 16, display: "flex", flexDirection: "column", gap: 12,
};

/** One scope's editor. Used for both the global and the per-company notice. */
function NoticeEditor({ scope, heading, hint, notice, onSaved, onError }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [removeImage, setRemoveImage] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  /* Reload the form whenever the scope or the stored record changes — switching
     company must not leave the previous client's words in the box. */
  useEffect(() => {
    setTitle(notice?.title || "");
    setBody(notice?.body || "");
    setFile(null);
    setRemoveImage(false);
    if (fileRef.current) fileRef.current.value = "";
  }, [scope, notice]);

  const storedImage = notice?.image?.dataUri || null;
  const dirty = title !== (notice?.title || "") || body !== (notice?.body || "") || !!file || removeImage;

  const pick = (e) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    /* Checked here only so the rejection is instant and readable; the server
       validates by MAGIC BYTES, which is what actually decides. */
    if (f.size > MAX_IMAGE_BYTES) {
      onError(`That image is ${(f.size / 1048576).toFixed(1)}MB — the limit is 2MB.`);
      e.target.value = "";
      return;
    }
    if (!ACCEPT.split(",").includes(f.type)) {
      onError(f.type === "image/svg+xml"
        ? "SVG images aren't accepted — they can carry script and this renders on the client's report. Use PNG, JPEG or WebP."
        : "Please choose a PNG, JPEG or WebP image.");
      e.target.value = "";
      return;
    }
    setFile(f);
    setRemoveImage(false);
  };

  const save = async () => {
    setBusy(true);
    try {
      /* ⚠ `image` is only sent when it actually changes. Sending null on every
         save would delete the banner each time Craig fixed a typo. */
      const saved = await saveNotice(scope, {
        title, body,
        image: file || undefined,
        removeImage: removeImage && !file,
      });
      onSaved(scope, saved);
    } catch (e) {
      onError(e?.response?.data?.error || e.message || "Could not save the notice");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try { await clearNotice(scope); onSaved(scope, null); }
    catch (e) { onError(e?.response?.data?.error || e.message || "Could not clear the notice"); }
    finally { setBusy(false); }
  };

  return (
    <div style={card}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14 }}>{heading}</div>
        <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 2 }}>{hint}</div>
      </div>

      <div>
        <label style={lbl}>Title <span style={{ fontWeight: 400, color: "var(--text3)" }}>— becomes the section heading on the report</span></label>
        <input className="inp" style={{ marginBottom: 0, width: "100%" }} maxLength={120}
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. New computer offer — ends 30 September" />
      </div>

      <div>
        <label style={lbl}>
          Message <span style={{ fontWeight: 400, color: "var(--text3)" }}>
            — <b>**bold**</b>, [links](https://example.com), blank line for a new paragraph
          </span>
        </label>
        <textarea className="inp" style={{ marginBottom: 0, width: "100%", minHeight: 120, fontFamily: "inherit", lineHeight: 1.5 }}
          maxLength={4000} value={body} onChange={(e) => setBody(e.target.value)}
          placeholder={"We've secured a bulk price on the HP ProBook 450...\n\nAsk us for a quote before the end of the month."} />
        <div style={{ fontSize: 10.5, color: "var(--text3)", marginTop: 3 }}>{body.length} / 4000</div>
      </div>

      <div>
        <label style={lbl}>Banner image <span style={{ fontWeight: 400, color: "var(--text3)" }}>— optional · PNG, JPEG or WebP · up to 2MB</span></label>
        {storedImage && !removeImage && !file && (
          <div style={{ marginBottom: 8 }}>
            <img src={storedImage} alt="" style={{ maxWidth: "100%", maxHeight: 130, borderRadius: 8, display: "block", border: "1px solid var(--border)" }} />
            <button className="btn" style={{ fontSize: 11, marginTop: 6 }} onClick={() => { setRemoveImage(true); setFile(null); }}>
              Remove image
            </button>
          </div>
        )}
        {removeImage && (
          <div style={{ fontSize: 11.5, color: "var(--warn,#b8860b)", marginBottom: 6 }}>
            Image will be removed when you save. <button className="btn" style={{ fontSize: 11, marginLeft: 6 }} onClick={() => setRemoveImage(false)}>Undo</button>
          </div>
        )}
        {file && <div style={{ fontSize: 11.5, color: "var(--text2)", marginBottom: 6 }}>New image: {file.name} ({(file.size / 1024).toFixed(0)} KB)</div>}
        <input ref={fileRef} type="file" accept={ACCEPT} onChange={pick} style={{ fontSize: 11.5 }} />
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button className="btn btn-strat" disabled={busy || !dirty || !title.trim()} onClick={save}>
          {busy ? "Saving…" : "Save notice"}
        </button>
        {notice && (
          <button className="btn" disabled={busy} onClick={remove} style={{ color: "var(--danger)" }}>
            Clear notice
          </button>
        )}
        {/* Saving IS publishing here — say so, rather than letting Craig assume
            there is a draft step like Did You Know and Secure Score have. */}
        <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: "auto" }}>
          {notice ? "Live on this month's reports" : "Nothing set — the section is omitted"}
        </span>
      </div>
    </div>
  );
}

export default function ReportNotices({ companies = [], onMessage }) {
  const [global, setGlobal] = useState(null);
  const [withNotice, setWithNotice] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [companyNotice, setCompanyNotice] = useState(null);
  const [loading, setLoading] = useState(true);

  const ids = companies.map((c) => String(c.id));
  const idsKey = ids.join(",");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getNotices(idsKey ? idsKey.split(",") : []);
      setGlobal(data.global || null);
      setWithNotice(data.companies || []);
    } catch (e) {
      onMessage?.({ kind: "err", text: e?.response?.data?.error || e.message });
    } finally { setLoading(false); }
  }, [idsKey, onMessage]);

  useEffect(() => { load(); }, [load]);

  /* The per-company editor reads from the list the server already returned, so
     picking a company costs no extra round trip. */
  useEffect(() => {
    if (!companyId) { setCompanyNotice(null); return; }
    const row = withNotice.find((r) => String(r.companyId) === String(companyId));
    if (!row) { setCompanyNotice(null); return; }
    // The list carries a summary; fetch the full record (body + image) on pick.
    import("../../api/monthlyReportsApi").then(({ getNotice }) =>
      getNotice(String(companyId)).then(setCompanyNotice).catch(() => setCompanyNotice(null)));
  }, [companyId, withNotice]);

  const onSaved = (scope, saved) => {
    onMessage?.({ kind: "ok", text: saved ? "Notice saved — it is live on this month's reports." : "Notice cleared." });
    if (scope === "global") setGlobal(saved);
    else setCompanyNotice(saved);
    load();
  };
  const onError = (text) => onMessage?.({ kind: "err", text });

  const nameOf = (cid) => companies.find((c) => String(c.id) === String(cid))?.companyName || cid;

  if (loading) return <div style={{ fontSize: 12, color: "var(--text3)" }}>Loading notices…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ fontSize: 12, color: "var(--text2)", lineHeight: 1.6 }}>
        A notice renders <b>directly under the Executive Summary</b> on the client report, on every plan.
        {" "}A client with both a global and its own notice sees <b>both</b> — global first.
        {" "}Nothing renders when nothing is set.
      </div>

      <NoticeEditor
        scope="global"
        heading="Global notice — every client"
        hint="Goes on every report you generate. Use it for offers and general announcements."
        notice={global}
        onSaved={onSaved}
        onError={onError}
      />

      <div style={card}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Per-client notice</div>
          <div style={{ fontSize: 11.5, color: "var(--text3)", marginTop: 2 }}>
            Aimed at one client. It appears <b>below</b> the global notice, not instead of it.
          </div>
        </div>
        <div>
          <label style={lbl}>Client</label>
          <select className="inp" style={{ marginBottom: 0, width: "100%", maxWidth: 380 }}
            value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">— pick a client —</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.companyName}{withNotice.some((r) => String(r.companyId) === String(c.id)) ? "  ● has a notice" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {companyId && (
        <NoticeEditor
          key={companyId}
          scope={String(companyId)}
          heading={`Notice for ${nameOf(companyId)}`}
          hint="Only this client sees it."
          notice={companyNotice}
          onSaved={onSaved}
          onError={onError}
        />
      )}

      {/* Which clients currently carry one — otherwise a notice set months ago
          on one client is invisible until it turns up on a report. */}
      <div style={card}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Clients with a notice set ({withNotice.length})</div>
        {withNotice.length === 0
          ? <div style={{ fontSize: 12, color: "var(--text3)" }}>None. Only the global notice (if set) will appear.</div>
          : withNotice.map((r) => (
            <div key={r.companyId} style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 12, padding: "5px 0", borderTop: "1px solid var(--border)" }}>
              <button className="btn" style={{ fontSize: 11 }} onClick={() => setCompanyId(String(r.companyId))}>Edit</button>
              <b>{nameOf(r.companyId)}</b>
              <span style={{ color: "var(--text2)" }}>{r.title}</span>
              {r.hasImage && <span style={{ fontSize: 10, color: "var(--text3)" }}>🖼 image</span>}
            </div>
          ))}
      </div>
    </div>
  );
}
